import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Paper,
  Button,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import PersonIcon from "@mui/icons-material/Person";
import axiosInstance from "API/axiosInstance";
import { decryptText } from "config/encryption";
import MDButton from "components/MDButton";
import { API_ENDPOINTS } from "config/api";

// Get staffId from localStorage
const getAdminSession = () => {
  try {
    const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (session) {
      const decrypted = decryptText(session);
      if (decrypted) {
        const parsed = JSON.parse(decrypted);
        return parsed.StaffId || parsed.staffId || parsed.StaffCode || parsed.staffCode || null;
      }
    }
  } catch (error) {
    console.error("Error extracting admin session:", error);
  }
  return null;
};

const AdminScreenRightsManager = ({ open, onClose }) => {
  const [staffList, setStaffList] = useState([]);
  const [screens, setScreens] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffScreenRights, setStaffScreenRights] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingRights, setLoadingRights] = useState(false);
  const [loadingStaffList, setLoadingStaffList] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const currentStaffId = getAdminSession();

  useEffect(() => {
    if (open) {
      fetchScreens();
      fetchStaffList();
    }
  }, [open]);

  const fetchStaffList = async () => {
    try {
      setLoadingStaffList(true);
      const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_STAFF_LIST_FOR_SCREEN_RIGHTS);
      if (response.data && Array.isArray(response.data)) {
        const mappedStaffList = response.data.map((staff) => ({
          staffId: staff.sid || staff.StaffId || staff.staffId,
          staffCode: staff.scode || staff.StaffCode || staff.staffCode,
          staffName: staff.staffname || staff.StaffName || staff.staffName || staff.staffCode || staff.scode,
        }));
        setStaffList(mappedStaffList);
      }
    } catch (error) {
      console.error("Error fetching staff list:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch staff list",
        severity: "error",
      });
    } finally {
      setLoadingStaffList(false);
    }
  };

  const fetchScreens = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_ALL_SCREENS);
      if (response.data && response.data.screens) {
        setScreens(response.data.screens);
      }
    } catch (error) {
      console.error("Error fetching screens:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch screens",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffScreenRights = async (staffId) => {
    if (!staffId) return;
    
    try {
      setLoadingRights(true);
      const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_STAFF_SCREEN_RIGHTS(staffId));
      if (response.data && Array.isArray(response.data)) {
        const rightsMap = {};
        response.data.forEach((right) => {
          rightsMap[right.screenId] = {
            hasAccess: right.hasAccess || right.HasAccess || false,
            canView: right.canView || right.CanView || false,
            canAdd: right.canAdd || right.CanAdd || false,
            canEdit: right.canEdit || right.CanEdit || false,
            canDelete: right.canDelete || right.CanDelete || false,
            screenCode: right.screenCode || right.ScreenCode || '',
            screenName: right.screenName || right.ScreenName || '',
          };
        });
        setStaffScreenRights(rightsMap);
      } else {
        setStaffScreenRights({});
      }
    } catch (error) {
      console.error("Error fetching staff screen rights:", error);
      setStaffScreenRights({});
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch screen rights",
        severity: "error",
      });
    } finally {
      setLoadingRights(false);
    }
  };

  const handleStaffSelect = (staff) => {
    if (staff) {
      setSelectedStaff(staff);
      fetchStaffScreenRights(staff.staffId);
      setUnsavedChanges(false);
    } else {
      setSelectedStaff(null);
      setStaffScreenRights({});
      setUnsavedChanges(false);
    }
  };

  const handlePermissionChange = (screenId, permission, value) => {
    setUnsavedChanges(true);
    setStaffScreenRights((prev) => {
      const updated = { ...prev };
      if (!updated[screenId]) {
        updated[screenId] = {
          hasAccess: false,
          canView: false,
          canAdd: false,
          canEdit: false,
          canDelete: false,
          screenCode: screens.find((s) => s.screenId === screenId)?.screenCode || '',
          screenName: screens.find((s) => s.screenId === screenId)?.screenName || '',
        };
      }
      updated[screenId][permission] = value;
      
      // If hasAccess is false, disable all other permissions
      if (permission === 'hasAccess' && !value) {
        updated[screenId].canView = false;
        updated[screenId].canAdd = false;
        updated[screenId].canEdit = false;
        updated[screenId].canDelete = false;
      }
      
      // If hasAccess is true, ensure canView is also true
      if (permission === 'hasAccess' && value) {
        updated[screenId].canView = true;
      }
      
      return updated;
    });
  };

  const handleBulkSave = async () => {
    if (!selectedStaff || !currentStaffId) {
      setSnackbar({
        open: true,
        message: "Please select a staff member",
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const screenRightsArray = screens.map((screen) => {
        const right = staffScreenRights[screen.screenId] || {
          hasAccess: false,
          canView: false,
          canAdd: false,
          canEdit: false,
          canDelete: false,
        };
        return {
          screenId: screen.screenId,
          hasAccess: right.hasAccess,
          canView: right.canView,
          canAdd: right.canAdd,
          canEdit: right.canEdit,
          canDelete: right.canDelete,
        };
      });

      await axiosInstance.post(API_ENDPOINTS.ADMIN.BULK_UPDATE_STAFF_SCREEN_RIGHTS, {
        staffId: selectedStaff.staffId,
        screenRights: screenRightsArray,
        modifiedBy: parseInt(currentStaffId),
      });

      setSnackbar({
        open: true,
        message: "Screen rights updated successfully",
        severity: "success",
      });
      setUnsavedChanges(false);
      fetchStaffScreenRights(selectedStaff.staffId);
    } catch (error) {
      console.error("Error saving screen rights:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to save screen rights",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getScreenRight = (screenId) => {
    return staffScreenRights[screenId] || {
      hasAccess: false,
      canView: false,
      canAdd: false,
      canEdit: false,
      canDelete: false,
    };
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      {/* Simple Header */}
      <DialogTitle
        sx={{
          bgcolor: 'success.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 3,
        }}
      >
        <Typography variant="body1" fontWeight="bold" fontSize="0.95rem">
          Screen Rights Management
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white', padding: '4px' }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

        {/* Staff Selector */}
      <DialogContent sx={{ p: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: 'grey.50',
            borderRadius: 1,
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={staffList}
                getOptionLabel={(option) => option.staffCode || `Staff ${option.staffId}`}
                value={selectedStaff}
                onChange={(event, newValue) => {
                  handleStaffSelect(newValue);
                }}
                loading={loadingStaffList}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Staff Member"
                    placeholder="Search by staff code..."
                    fullWidth
                    size="small"
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium" fontSize="0.85rem">
                        {option.staffCode}
                      </Typography>
                      {option.staffName && (
                        <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                          {option.staffName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option.staffId === value?.staffId}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                {selectedStaff && (
                  <>
                    <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                      Managing: <strong>{selectedStaff.staffCode}</strong>
                      {selectedStaff.staffName && ` (${selectedStaff.staffName})`}
                    </Typography>
                    {unsavedChanges && (
                      <Typography variant="caption" color="warning.main" sx={{ fontWeight: 'bold' }} fontSize="0.75rem">
                        • Unsaved Changes
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Screens List */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
              Loading screens...
            </Typography>
          </Box>
        ) : !selectedStaff ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5, opacity: 0.5 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom fontSize="0.9rem" fontWeight="medium">
              Select a Staff Member
            </Typography>
            <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
              Choose a staff member from the dropdown above to manage their permissions
            </Typography>
          </Box>
        ) : loadingRights ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
              Loading permissions...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
            {screens.map((screen, index) => {
              const right = getScreenRight(screen.screenId);
              return (
                <Paper
                  key={screen.screenId}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Grid container spacing={1.5} alignItems="center">
                    {/* Screen Name */}
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" fontWeight="medium" fontSize="0.85rem">
                        {screen.screenName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                        {screen.screenCode}
                      </Typography>
                    </Grid>

                    {/* Permissions */}
                    <Grid item xs={12} md={8}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6} sm={4} md={2.4}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Checkbox
                              checked={right.hasAccess}
                              onChange={(e) => handlePermissionChange(screen.screenId, 'hasAccess', e.target.checked)}
                              size="small"
                              color="primary"
                              sx={{ padding: '4px' }}
                            />
                            <Typography variant="body2" fontSize="0.8rem">Access</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2.4}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Checkbox
                              checked={right.canView}
                              onChange={(e) => handlePermissionChange(screen.screenId, 'canView', e.target.checked)}
                              disabled={!right.hasAccess}
                              size="small"
                              color="primary"
                              sx={{ padding: '4px' }}
                            />
                            <Typography variant="body2" fontSize="0.8rem">View</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2.4}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Checkbox
                              checked={right.canAdd}
                              onChange={(e) => handlePermissionChange(screen.screenId, 'canAdd', e.target.checked)}
                              disabled={!right.hasAccess || !right.canView}
                              size="small"
                              color="primary"
                              sx={{ padding: '4px' }}
                            />
                            <Typography variant="body2" fontSize="0.8rem">Add</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2.4}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Checkbox
                              checked={right.canEdit}
                              onChange={(e) => handlePermissionChange(screen.screenId, 'canEdit', e.target.checked)}
                              disabled={!right.hasAccess || !right.canView}
                              size="small"
                              color="primary"
                              sx={{ padding: '4px' }}
                            />
                            <Typography variant="body2" fontSize="0.8rem">Edit</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2.4}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Checkbox
                              checked={right.canDelete}
                              onChange={(e) => handlePermissionChange(screen.screenId, 'canDelete', e.target.checked)}
                              disabled={!right.hasAccess || !right.canView}
                              size="small"
                              color="primary"
                              sx={{ padding: '4px' }}
                            />
                            <Typography variant="body2" fontSize="0.8rem">Delete</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Box>
        )}
      </DialogContent>

      {/* Footer Actions */}
      {selectedStaff && (
        <>
          <Divider />
          <DialogActions sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}>
            <Button onClick={onClose} variant="outlined" color="inherit" size="small" sx={{ fontSize: '0.85rem' }}>
              Cancel
            </Button>
            <MDButton
              color="info"
              variant="gradient"
              startIcon={<SaveIcon sx={{ fontSize: '1rem' }} />}
              onClick={handleBulkSave}
              disabled={loading || !unsavedChanges}
              size="small"
              sx={{ fontSize: '0.85rem' }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </MDButton>
          </DialogActions>
        </>
      )}

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

AdminScreenRightsManager.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminScreenRightsManager;
